import type { MaterialNodeSpec, OperationNodeSpec, Quantity } from '../../graph/domain/types';
import type { Store } from '../types';
import { resolveYieldFactor } from '@/commands/helpers/propagateDownstream';

type GetState = () => Store;

export interface AddStepInput {
  sourceMaterialId: string;
  operationSpec: OperationNodeSpec;
  downstreamMaterialOverrides?: Partial<MaterialNodeSpec>;
}

export interface AddStepResult {
  operationId: string;
  downstreamMaterialId: string;
}

function deriveDownstreamName(source: MaterialNodeSpec): string {
  return source.name;
}

/**
 * Calculate downstream material quantity based on operation archetype and yield.
 *
 * For TRANSFORM/MEASURE operations, apply the normalized yield factor.
 * For TRANSFER/COMPLEX, pass through source quantity unchanged.
 */
function calculateDownstreamQuantity(
  sourceMaterial: MaterialNodeSpec,
  operationSpec: OperationNodeSpec
): Quantity | null {
  // No source quantity → no calculation
  if (!sourceMaterial.quantity) {
    return null;
  }

  const sourceQty = sourceMaterial.quantity;

  if (operationSpec.archetype === 'TRANSFORM' || operationSpec.archetype === 'MEASURE') {
    const yieldMultiplier = resolveYieldFactor(operationSpec);

    // Handle string/number conversion for amount
    const sourceAmount = (() => {
      const val = sourceQty.value.amount;
      if (typeof val === 'number') return val;
      if (typeof val === 'string') {
        const parsed = parseFloat(val);
        return Number.isFinite(parsed) ? parsed : 0;
      }
      return 0;
    })();

    const outputAmount = sourceAmount * yieldMultiplier;
    return {
      ...sourceQty,
      value: {
        ...sourceQty.value,
        amount: outputAmount,
      },
    };
  }

  // For TRANSFER and COMPLEX: pass through source quantity
  return sourceQty;
}

export function createStepActions(get: GetState) {
  return {
    addStep(input: AddStepInput): AddStepResult {
      const sourceMaterial = get().graphData.materialNodes[input.sourceMaterialId];
      if (!sourceMaterial) {
        throw new Error(`Source material not found: ${input.sourceMaterialId}`);
      }

      // Compute correct global creationOrder (max existing + 1)
      let maxOrder = 0;
      for (const op of Object.values(get().graphData.operationNodes)) {
        maxOrder = Math.max(maxOrder, op.creationOrder ?? 0);
      }
      const nextOrder = maxOrder + 1;

      // 1. Create operation. Position is owned by the HierarchicalLayout.
      const operationId = get().addOperationNode({
        ...input.operationSpec,
        creationOrder: nextOrder,
      });

      // 2. Create downstream material with calculated quantity
      const calculatedQuantity = calculateDownstreamQuantity(sourceMaterial, input.operationSpec);

      const downstream: MaterialNodeSpec = {
        name: deriveDownstreamName(sourceMaterial),
        container: { ...sourceMaterial.container, id: '' },
        constituents: [...sourceMaterial.constituents],
        sourceMaterials: [
          {
            sourceVertexId: input.sourceMaterialId,
            name: sourceMaterial.name,
            container: sourceMaterial.container,
            notes: null,
          },
        ],
        quantity: calculatedQuantity,
        isEmpty: false,
        // Position is owned by the HierarchicalLayout; no manual seeding.
        ...input.downstreamMaterialOverrides,
      };
      const downstreamMaterialId = get().addMaterialNode(downstream);

      // 3. Create edges
      get().connectMaterialToOperation(input.sourceMaterialId, operationId);
      get().connectOperationToMaterial(operationId, downstreamMaterialId);

      return { operationId, downstreamMaterialId };
    },
  };
}
